import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { ArrowRight, Mic, Users, Building2, GraduationCap, Calendar, CheckCircle } from 'lucide-react';
import { motion } from 'framer-motion';

export const BookCameronPage = () => {
  const engagementTypes = [
    {
      icon: Mic,
      title: 'Keynote Speaking',
      desc: 'Inspire your audience with powerful presentations on leadership, systems thinking, and school transformation.',
      formats: ['Conference keynotes', 'District convocations', 'Leadership summits', 'Award ceremonies'],
    },
    {
      icon: Users,
      title: 'Leadership Retreats',
      desc: 'Facilitate transformative experiences for your leadership team, board, or district cabinet.',
      formats: ['Board retreats', 'Cabinet retreats', 'Principal academies', 'Strategic planning sessions'],
    },
    {
      icon: Building2,
      title: 'Professional Development',
      desc: 'Deliver targeted training on leadership systems, accountability, and school improvement.',
      formats: ['Half-day workshops', 'Full-day intensives', 'Multi-day institutes', 'Series training'],
    },
    {
      icon: GraduationCap,
      title: 'Executive Coaching',
      desc: 'One-on-one coaching for superintendents, principals, and district leaders seeking personalized development.',
      formats: ['Superintendent coaching', 'Principal mentoring', 'New leader onboarding', 'Transition support'],
    },
  ];

  const topics = [
    'Building High-Performing Leadership Teams',
    'Systems Thinking for School Improvement',
    'Achieving 100% College & Career Readiness',
    'Turnaround Leadership: From Failing to Thriving',
    'Governance in Alignment: Board-Superintendent Excellence',
    'Crisis Leadership & De-Escalation',
    'Middle Grades Transformation Strategies',
    'CTE Leadership & Workforce Development',
    'Military Leadership Principles in Education',
    'Creating Sustainable School Legacies',
  ];

  return (
    <div className="pt-20 lg:pt-24" data-testid="book-cameron-page">
      {/* Hero Section */}
      <section className="section-padding bg-[#F7F8F6]" data-testid="book-hero">
        <div className="container-premium">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
            >
              <p className="overline mb-4">Speaking & Consulting</p>
              <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-bold text-[#172033] leading-tight mb-6">
                Book Cameron
              </h1>
              <p className="text-slate-600 text-lg lg:text-xl leading-relaxed mb-8">
                Bring Captain Cameron Whitlow to your district, conference, or leadership event. Inspire your team with practical insights from 25+ years of educational leadership experience.
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <Link to="/contact">
                  <Button className="bg-[#0B1F3A] hover:bg-[#172033] text-white rounded-full px-8 py-4 font-body">
                    Request Availability
                    <ArrowRight className="ml-2 w-4 h-4" />
                  </Button>
                </Link>
                <Button variant="outline" className="border-2 border-[#0B1F3A] text-[#0B1F3A] hover:bg-[#0B1F3A] hover:text-white rounded-full px-8 py-4 font-body">
                  Download Speaker Kit
                </Button>
              </div>
            </motion.div>
            
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="relative"
            >
              <div className="aspect-[4/5] rounded-2xl overflow-hidden shadow-2xl">
                <img 
                  src="https://customer-assets.emergentagent.com/job_instructional-lead/artifacts/eyi4u393_83285.jpg"
                  alt="Captain Cameron Whitlow - Speaker"
                  className="w-full h-full object-cover"
                />
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Engagement Types */}
      <section className="section-padding bg-white" data-testid="engagement-types">
        <div className="container-premium">
          <div className="max-w-2xl mb-16">
            <p className="overline mb-4">How We Work Together</p>
            <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-bold text-[#172033] mb-6">
              Engagement Options
            </h2>
            <p className="text-slate-600 text-lg leading-relaxed">
              From keynote addresses to multi-year consulting partnerships, Cameron offers flexible engagement options to meet your needs.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-6 lg:gap-8">
            {engagementTypes.map((type, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="card-premium hover-lift"
                data-testid={`engagement-${index}`}
              >
                <div className="w-12 h-12 bg-[#4F8A2F]/20 rounded-xl flex items-center justify-center mb-6">
                  <type.icon className="w-6 h-6 text-[#4F8A2F]" strokeWidth={1.5} />
                </div>
                <h3 className="font-display text-2xl font-bold text-[#172033] mb-3">{type.title}</h3>
                <p className="text-slate-600 leading-relaxed mb-6">{type.desc}</p>
                <div className="flex flex-wrap gap-2">
                  {type.formats.map((format, i) => (
                    <span key={i} className="text-xs bg-[#F7F8F6] text-slate-600 px-3 py-1.5 rounded-full">
                      {format}
                    </span>
                  ))}
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Speaking Topics */}
      <section className="section-padding bg-[#0B1F3A]" data-testid="topics-section">
        <div className="container-premium">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-start">
            <div>
              <p className="overline text-[#4F8A2F] mb-4">Signature Topics</p>
              <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-bold text-white mb-6">
                Popular Speaking Topics
              </h2>
              <p className="text-slate-300 text-lg leading-relaxed mb-8">
                Each presentation is customized to your audience, context, and goals. Cameron delivers practical, actionable insights—not theory.
              </p>
              <Link to="/contact">
                <Button className="bg-[#4F8A2F] hover:bg-[#6FAE3A] text-[#0B1F3A] rounded-full px-8 py-4 font-body">
                  Request Custom Topic
                  <ArrowRight className="ml-2 w-4 h-4" />
                </Button>
              </Link>
            </div>
            <div>
              <ul className="space-y-3">
                {topics.map((topic, index) => (
                  <motion.li
                    key={index}
                    initial={{ opacity: 0, x: 20 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.4, delay: index * 0.05 }}
                    className="flex items-center gap-3 bg-white/5 backdrop-blur px-4 py-3 rounded-xl"
                  >
                    <CheckCircle className="w-5 h-5 text-[#4F8A2F] flex-shrink-0" strokeWidth={2} />
                    <span className="text-white">{topic}</span>
                  </motion.li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Consulting Packages */}
      <section className="section-padding bg-white" data-testid="packages-section">
        <div className="container-premium">
          <div className="text-center mb-16">
            <p className="overline mb-4">Consulting Partnerships</p>
            <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-bold text-[#172033] mb-6">
              Flexible Engagement Formats
            </h2>
            <p className="text-slate-600 text-lg max-w-2xl mx-auto">
              From one-day intensives to multi-year transformation partnerships, we offer formats that fit your timeline and budget.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-6">
            {[
              { title: 'Half-Day Intensive', duration: '4 hours', desc: 'Focused workshop on a single topic or challenge', price: 'Starting at $2,500' },
              { title: 'Full-Day Workshop', duration: '8 hours', desc: 'Deep dive into leadership systems with action planning', price: 'Starting at $4,500', featured: true },
              { title: 'Multi-Year Partnership', duration: '12-36 months', desc: 'Comprehensive district transformation engagement', price: 'Custom Pricing' },
            ].map((pkg, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className={`rounded-2xl p-8 text-center ${
                  pkg.featured 
                    ? 'bg-[#0B1F3A] text-white' 
                    : 'bg-[#F7F8F6]'
                }`}
              >
                {pkg.featured && (
                  <span className="inline-block text-xs font-semibold tracking-wider uppercase text-[#4F8A2F] bg-[#4F8A2F]/20 px-3 py-1 rounded-full mb-4">
                    Most Popular
                  </span>
                )}
                <h3 className={`font-display text-2xl font-bold mb-2 ${pkg.featured ? 'text-white' : 'text-[#172033]'}`}>
                  {pkg.title}
                </h3>
                <p className={`text-sm mb-4 ${pkg.featured ? 'text-[#4F8A2F]' : 'text-[#4F8A2F]'}`}>
                  {pkg.duration}
                </p>
                <p className={`text-sm leading-relaxed mb-6 ${pkg.featured ? 'text-slate-300' : 'text-slate-600'}`}>
                  {pkg.desc}
                </p>
                <p className={`font-display text-xl font-bold mb-6 ${pkg.featured ? 'text-white' : 'text-[#172033]'}`}>
                  {pkg.price}
                </p>
                <Link to="/contact">
                  <Button 
                    className={`w-full rounded-full font-body ${
                      pkg.featured 
                        ? 'bg-[#4F8A2F] hover:bg-[#6FAE3A] text-[#0B1F3A]' 
                        : 'bg-[#0B1F3A] hover:bg-[#172033] text-white'
                    }`}
                  >
                    Request Quote
                  </Button>
                </Link>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="section-padding bg-[#F7F8F6]">
        <div className="container-premium text-center">
          <p className="overline mb-4">Ready to Book?</p>
          <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-bold text-[#172033] mb-6">
            Let's Start the Conversation
          </h2>
          <p className="text-slate-600 text-lg max-w-2xl mx-auto mb-10">
            Share your event details and goals. We'll respond within 48 hours with availability and a customized proposal.
          </p>
          <Link to="/contact">
            <Button className="bg-[#0B1F3A] hover:bg-[#172033] text-white rounded-full px-10 py-5 font-body text-lg">
              Request Availability
              <ArrowRight className="ml-2 w-5 h-5" />
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
};
